import React from 'react';
import { Stack, Flex, IconButton, Button, Text } from '@chakra-ui/react';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';

const PageButton = ({ page, isCurrent, isDisabled, onClick, ...rest }) => (
  <Button
    size="sm"
    variant={isCurrent ? "solid" : "outline"}
    colorScheme={isCurrent ? "blue" : "gray"}
    isDisabled={isDisabled}
    onClick={() => onClick(page)}
    minW={8}
    h={8}
    borderRadius="full"
    aria-label={`Ir para página ${page}`}
    {...rest}
  >
    {page}
  </Button>
);

const Ellipsis = () => (
  <Flex
    align="center"
    justify="center"
    minW={8}
    h={8}
    color="gray.400"
  >
    <Text fontSize="sm">...</Text>
  </Flex>
);

const Paginator = ({ 
  page, 
  hasNext, 
  onPaginate, 
  isFetching, 
  totalPages,
  maxVisiblePages = 7,
  ...rest 
}) => {
  const currentPage = parseInt(page);
  const total = parseInt(totalPages) || 1;

  // Generate page numbers to display
  const generatePageNumbers = () => {
    if (!totalPages || total <= maxVisiblePages) {
      // Show all pages if total is small
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages = [];
    
    // Calculate how many pages we can show around the current page
    const availableSlots = maxVisiblePages - 2; // Reserve 2 slots for first and last
    const halfSlots = Math.floor(availableSlots / 2);
    
    // Determine the range of pages to show around current page
    let startPage = Math.max(2, currentPage - halfSlots);
    let endPage = Math.min(total - 1, currentPage + halfSlots);
    
    // Adjust if we're near the edges
    if (currentPage <= halfSlots + 1) {
      // Near the start, show more pages after current
      startPage = 2;
      endPage = Math.min(total - 1, availableSlots);
    } else if (currentPage >= total - halfSlots) {
      // Near the end, show more pages before current
      startPage = Math.max(2, total - availableSlots);
      endPage = total - 1;
    }
    
    // Always include first page
    pages.push(1);
    
    // Add ellipsis if there's a gap after first page
    if (startPage > 2) {
      pages.push('ellipsis');
    }
    
    // Add pages in the middle range
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    // Add ellipsis if there's a gap before last page
    if (endPage < total - 1) {
      pages.push('ellipsis');
    }
    
    // Always include last page (unless it's the same as current page)
    if (total > 1) {
      pages.push(total);
    }

    return pages;
  };

  const pageNumbers = generatePageNumbers();

  return (
    <Stack as={Flex} direction={'row'} justify="center" align="center" spacing={2} {...rest}>
      <IconButton
        aria-label="Ir para página anterior"
        variant="outline"
        size="sm"
        isRound
        onClick={() => onPaginate(Math.max(currentPage - 1, 1))}
        isDisabled={currentPage === 1 || isFetching}
        icon={<FaArrowLeft/>}
      />
      
      {pageNumbers.map((pageNum, index) => (
        <React.Fragment key={index}>
          {pageNum === 'ellipsis' ? (
            <Ellipsis />
          ) : (
            <PageButton
              page={pageNum}
              isCurrent={pageNum === currentPage}
              isDisabled={isFetching}
              onClick={onPaginate}
            />
          )}
        </React.Fragment>
      ))}
      
      <IconButton
        aria-label="Ir para página posterior"
        variant="outline"
        size="sm"
        isRound
        onClick={() => onPaginate(Math.min(currentPage + 1, total))}
        isDisabled={currentPage >= total || isFetching}
        icon={<FaArrowRight/>}
      />
    </Stack>
  );
};

export default Paginator;
